import { useLazyQuery } from '@apollo/client/react'
import React, { Dispatch, FC, SetStateAction, useContext, useEffect, useState } from 'react'
import { PileNode, PileNodeConnection } from '@src/generated/schema'
import { useTranslation } from 'react-i18next'
import { TableProps as AntdTableProps } from 'antd/lib/table'
import { FullPageLoadingWithDataWrapper, LoadingWithoutDataWrapper, StyledButton } from './Table.styles'
import { IonCol, IonIcon, IonSpinner, IonTitle } from '@ionic/react'
import { Tag, TagType } from '@src/components/Elements/Tag'
import {
  add,
  checkmark,
  documentOutline,
  documentsOutline,
  funnel,
  mapOutline,
  createOutline,
  trashOutline,
  cloudUploadOutline,
  codeDownloadOutline,
} from 'ionicons/icons'
import { Pagination } from '@src/components/Desktop/Elements'
import { Alert, Tooltip } from 'antd'
import { ModalData, ModalEnum } from '../List'
import {
  StyledTableHeaderActions,
  StyledResponsiveTableHeaderButton,
  StyledTableHeaderButton,
} from '@src/components/Desktop/Containers/TableHeaderActions'
import { FilterForm } from '../FilterForm/FilterForm'
import { DrawerButton } from '@src/components/Elements/DrawerButton'
import { Pile } from '@src/graphql/Pile'
import { MeContext, Permissions, TOAST_TYPES, ToastContext } from '@src/components/Providers'
import { PileStatus } from '@src/graphql/Pile/Types'
import { VirtualizedTable } from '@src/components/Desktop/Containers/VirtualizedTable'
import { ColumnDef } from '@tanstack/react-table'
import { SortOrder } from 'antd/es/table/interface'

interface TableProps extends AntdTableProps<any> {
  itemsPerPage: number
  onItemClick?: (event: React.MouseEvent, row: PileNode, rowIndex: number) => void
  openModal: (modalType: ModalEnum, modalData?: ModalData) => void
  selectedRows: PileNode[]
  setItemsPerPage: Dispatch<SetStateAction<number>>
  setSelectedRows: Dispatch<SetStateAction<PileNode[]>>
  setShowPopover: ({ id }: { id: string }) => void
  showPopover: { id: string }
}

export interface SortSettings {
  field: string
  sortOrder: SortOrder
}

export const Table: FC<TableProps> = ({ itemsPerPage, selectedRows, setSelectedRows, onItemClick, openModal }) => {
  const [piles, setPiles] = useState<PileNode[]>([])
  const { t, i18n } = useTranslation()
  const { userHasPermission, me } = useContext(MeContext)
  const { setToast } = useContext(ToastContext)
  const [sort, setSort] = useState<SortSettings | undefined>()
  const [queryVariables, setQueryVariables] = useState<any>({
    first: itemsPerPage,
  })
  const [page, setPage] = useState<number>(1)
  const [getPiles, { loading, error, data, refetch }] = useLazyQuery<{ piles: PileNodeConnection }>(
    Pile.getPaginatedPiles,
    {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'none',
    },
  )

  useEffect(() => {
    if (error) {
      setToast({
        type: TOAST_TYPES.ERROR,
        message: t('desktop.pages.piles.list.error.message'),
        description: t('desktop.pages.piles.list.error.description'),
      })
    }
  }, [error, setToast])

  useEffect(() => {
    if (data) {
      const { piles: { edges } = { edges: [] as any } } = data || {}
      setPiles(edges.map(({ node }) => node))
    }
  }, [data])

  useEffect(() => {
    setQueryVariables((currentQueryVariables) => ({
      ...currentQueryVariables,
      first: itemsPerPage,
      before: undefined,
      last: undefined,
      after: undefined,
    }))
    setPage(1)
  }, [itemsPerPage, setQueryVariables, setPage])

  useEffect(() => {
    getPiles({
      variables: {
        includeBroached: false,
        ...queryVariables,
      },
    })
  }, [queryVariables, getPiles])

  useEffect(() => {
    if (!sort) {
      setQueryVariables((previous) => ({ ...previous, orderBy: undefined }))
    } else {
      const sortIndicator = sort?.sortOrder === 'descend' ? '-' : ''

      setQueryVariables((previous) => ({ ...previous, orderBy: `${sortIndicator}${sort?.field}` }))
    }
  }, [sort, setQueryVariables])

  const onSortChange = (sorting: { id: string; desc: boolean }) => {
    setSort({ field: sorting.id, sortOrder: sorting.desc ? 'descend' : 'ascend' })
  }

  const onCellClicked = (e: React.MouseEvent, record: PileNode, index: number | undefined) => {
    if (onItemClick && index !== undefined) {
      onItemClick(e, record, index)
    }
  }

  useEffect(() => {
    const el = document
    const event = document.createEvent('HTMLEvents')

    event.initEvent('resize', true, false)
    el.dispatchEvent(event)
  }, [piles.length])

  const getTagType = (text: PileStatus | string) => {
    if (text === PileStatus.Disposable || text === PileStatus.InRemoval) return TagType.PRIMARY
    if (text === PileStatus.Locked) return TagType.ERROR

    return TagType.DEFAULT
  }

  const columns = React.useMemo(
    () =>
      [
        {
          id: 'edit',
          header: '',
          cell: ({ row }) => (
            <StyledTableHeaderButton
              size='large'
              type='link'
              onClick={() => openModal(ModalEnum.CHANGE, [row.original])}
              disabled={!(userHasPermission(Permissions.CAN_CHANGE_PILE) && me?.client?.canChangePlotPile)}
            >
              <IonIcon icon={createOutline} />
            </StyledTableHeaderButton>
          ),
          enableSorting: false,
          size: 50,
        },
        {
          id: 'plotNumber',
          header: t('desktop.pages.piles.list.columns.plotNumber') as string,
          accessorFn: (pile) => pile?.plot?.number,
          enableSorting: false,
          size: 90,
        },
        {
          id: 'number',
          header: t('desktop.pages.piles.list.columns.number') as string,
          accessorKey: 'number',
          size: 120,
        },
        {
          id: 'date',
          header: t('desktop.pages.piles.list.columns.date') as string,
          accessorFn: ({ date }) =>
            new Date(date).toLocaleDateString(i18n.language, {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            }),
          size: 110,
        },
        {
          id: 'location',
          header: t('desktop.pages.piles.list.columns.location') as string,
          accessorKey: 'location',
        },
        {
          id: 'district',
          header: t('desktop.pages.piles.list.columns.district') as string,
          accessorKey: 'district',
          size: 70,
        },
        {
          id: 'status',
          header: t('desktop.pages.piles.list.columns.status') as string,
          accessorKey: 'status',
          cell: ({ getValue }) =>
            !!getValue() && (
              <Tag
                type={getTagType(getValue() as any)}
                key={getValue() as any}
                value={getValue() as any}
                text={t(`desktop.pages.piles.list.status.${getValue()}`)}
              />
            ),
        },
        {
          id: 'woodtype',
          header: t('desktop.pages.piles.list.columns.woodtype') as string,
          accessorKey: 'woodtype',
          cell: ({ getValue }) => <span>{getValue() as any}</span>,
          size: 80,
        },
        {
          id: 'sort',
          header: t('desktop.pages.piles.list.columns.sort') as string,
          accessorKey: 'sort',
          size: 90,
        },
        {
          id: 'logLength',
          header: t('desktop.pages.piles.list.columns.logLength') as string,
          accessorKey: 'logLength',
          enableSorting: false,
          size: 90,
        },
        {
          id: 'rest',
          header: t('desktop.pages.piles.list.columns.rest') as string,
          accessorKey: 'rest',
          size: 90,
        },
        {
          id: 'cubikActual',
          header: t('desktop.pages.piles.list.columns.cubikActual') as string,
          accessorKey: 'cubikActual',
          enableSorting: false,
          size: 90,
        },
        {
          id: 'cubikSolidActual',
          header: t('desktop.pages.piles.list.columns.cubikSolidActual') as string,
          accessorKey: 'cubikSolidActual',
          enableSorting: false,
          size: i18n.language === 'de' ? 100 : 120,
        },
        {
          id: 'cbkNet',
          header: t('desktop.pages.piles.list.columns.cbkNet') as string,
          accessorKey: 'cbkNet',
          enableSorting: false,
          size: 100,
        },
        {
          id: 'cbkSolid',
          header: t('desktop.pages.piles.list.columns.cbkSolid') as string,
          accessorKey: 'cbkSolid',
          enableSorting: false,
          size: 100,
        },
        {
          id: 'category',
          header: t('desktop.pages.piles.list.columns.category') as string,
          accessorKey: 'category',
          enableSorting: false,
          size: 140,
        },
        {
          id: 'locationPlan',
          header: t('desktop.pages.piles.list.columns.locationPlan') as string,
          accessorKey: 'locationPlan',
          enableSorting: false,
        },
        {
          id: 'count',
          header: t('desktop.pages.piles.list.columns.count') as string,
          accessorKey: 'count',
          enableSorting: false,
          size: 90,
        },
        {
          id: 'countActual',
          header: t('desktop.pages.piles.list.columns.countActual') as string,
          accessorKey: 'countActual',
          enableSorting: false,
          size: 150,
        },
        {
          id: 'pileNumberFurnisher',
          header: t('desktop.pages.piles.list.columns.pileNumberFurnisher') as string,
          accessorKey: 'pileNumberFurnisher',
          enableSorting: false,
        },
        {
          id: 'isBroached',
          header: t('desktop.pages.piles.list.columns.isBroached'),
          size: 90,
          accessorKey: 'isBroached',
          cell: ({ getValue }) =>
            !!getValue() && (
              <>
                <IonIcon mode='md' icon={checkmark} size='small' />
              </>
            ),
          enableSorting: false,
        },
      ] as Array<ColumnDef<PileNode, any>>,
    [t, i18n.language],
  )

  return (
    <>
      {error && !data && (
        <Alert
          message={t('desktop.pages.piles.list.error.message')}
          description={t('desktop.pages.piles.list.error.description')}
          type='error'
          showIcon
        />
      )}

      {loading && (
        <>
          {piles.length === 0 ? (
            <LoadingWithoutDataWrapper>
              <IonCol className='ion-text-center'>
                <IonSpinner name='crescent' />
              </IonCol>
            </LoadingWithoutDataWrapper>
          ) : (
            <FullPageLoadingWithDataWrapper>
              <IonCol className='ion-text-center'>
                <IonSpinner name='crescent' />
              </IonCol>
            </FullPageLoadingWithDataWrapper>
          )}
        </>
      )}

      <StyledTableHeaderActions>
        {
          <StyledResponsiveTableHeaderButton
            size='large'
            type='link'
            onClick={() => openModal(ModalEnum.MAP, selectedRows)}
            disabled={!selectedRows.length}
          >
            <Tooltip title={t('desktop.pages.piles.list.popover.showOnMap')}>
              <IonIcon icon={mapOutline} />
            </Tooltip>
            {t('desktop.pages.piles.list.popover.showOnMap')}
          </StyledResponsiveTableHeaderButton>
        }
        {me?.client?.canExportPiles && (
          <StyledResponsiveTableHeaderButton
            size='large'
            type='link'
            onClick={() => openModal(ModalEnum.EXPORT_PILES)}
            disabled={!selectedRows.length}
          >
            <Tooltip title={t('desktop.pages.piles.exportPiles.title')}>
              <IonIcon icon={codeDownloadOutline} />
            </Tooltip>
            {t('desktop.pages.piles.exportPiles.title')}
          </StyledResponsiveTableHeaderButton>
        )}
        {
          <StyledResponsiveTableHeaderButton
            size='large'
            type='link'
            onClick={() => openModal(ModalEnum.TRANSFER_TO_ORDER, selectedRows)}
            disabled={!selectedRows.length}
          >
            <Tooltip title={t('desktop.pages.piles.list.popover.createOrder')}>
              <IonIcon icon={documentOutline} />
            </Tooltip>
            {t('desktop.pages.piles.list.popover.createOrder')}
          </StyledResponsiveTableHeaderButton>
        }
        {userHasPermission(Permissions.CAN_ADD_PILES_TO_EXISTING_ORDER) && (
          <StyledResponsiveTableHeaderButton
            size='large'
            type='link'
            onClick={() => openModal(ModalEnum.ADD_TO_EXISTING, selectedRows)}
            disabled={!selectedRows.length}
          >
            <Tooltip title={t('desktop.pages.piles.list.popover.AddPileToOrder')}>
              <IonIcon icon={documentsOutline} />
            </Tooltip>
            {t('desktop.pages.piles.list.popover.AddPileToOrder')}
          </StyledResponsiveTableHeaderButton>
        )}
        <StyledResponsiveTableHeaderButton
          size='large'
          type='link'
          onClick={() => openModal(ModalEnum.DELETE, selectedRows)}
          disabled={!selectedRows.length}
        >
          <Tooltip title={t('desktop.pages.piles.list.popover.delete')}>
            <IonIcon icon={trashOutline} />
          </Tooltip>
          {t('desktop.pages.piles.list.popover.delete')}
        </StyledResponsiveTableHeaderButton>
        {userHasPermission(Permissions.CAN_ADD_PILE) && me?.client?.canChangePlotPile && (
          <StyledResponsiveTableHeaderButton size='large' type='link' onClick={() => openModal(ModalEnum.CREATE)}>
            <Tooltip title={t('desktop.pages.piles.list.createPile')}>
              <IonIcon icon={add} />
            </Tooltip>
            {t('desktop.pages.piles.list.createPile')}
          </StyledResponsiveTableHeaderButton>
        )}
        {userHasPermission(Permissions.CAN_ADD_PILE) && me?.client?.canChangePlotPile && (
          <StyledTableHeaderButton size='large' type='link' onClick={() => openModal(ModalEnum.IMPORT_PILES)}>
            <IonIcon icon={cloudUploadOutline} />
            {t('desktop.pages.piles.list.uploadPiles')}
          </StyledTableHeaderButton>
        )}
        <DrawerButton icon={funnel} buttonText={t('desktop.pages.general.filter.button')}>
          <FilterForm
            setQueryVariables={setQueryVariables}
            setSelectedRows={setSelectedRows}
            setPage={setPage}
            itemsPerPage={itemsPerPage}
          />
        </DrawerButton>
      </StyledTableHeaderActions>
      <VirtualizedTable
        onCellClicked={onCellClicked}
        columns={columns}
        data={piles}
        setSelectedRows={setSelectedRows}
        onSortChange={onSortChange}
      />
      {data &&
        (data?.piles?.edges?.length > 0 ? (
          <>
            {!error && (
              <Pagination
                loading={loading}
                pageInfo={data.piles && data.piles.pageInfo}
                page={page}
                setPage={setPage}
                queryVariables={queryVariables}
                setQueryVariables={setQueryVariables}
              />
            )}

            {error && <StyledButton onClick={() => refetch()}>{t('general.buttonRefetch')}</StyledButton>}
          </>
        ) : (
          <IonTitle className='ion-text-center'>{t('desktop.pages.piles.list.noPilesAvailable')}</IonTitle>
        ))}
    </>
  )
}
